"use client";
import { createContext, useContext } from "react";
import { getMessagesForLocale } from "@/i18n/actions";
import { ReactNode, useCallback, useState, useEffect } from "react";
import { Locale, fullLocaleCodes, isRtlLocale } from "@/i18n/config";
import { NextIntlClientProvider, AbstractIntlMessages } from "next-intl";

/**
 * Context to expose the language switching function
 */
interface I18nContextType {
  locale: Locale;
  changeLocale: (newLocale: Locale) => Promise<void>;
  isLoading: boolean;
}

const I18nContext = createContext<I18nContextType | null>(null);

export const useI18n = () => {
  const context = useContext(I18nContext);
  if (!context) {
    throw new Error("useI18n must be used within a ClientI18nProvider");
  }
  return context;
};

/**
 * Props for the provider
 */
interface ClientI18nProviderProps {
  children: ReactNode;
  initialLocale: Locale;
  initialMessages: AbstractIntlMessages;
}

/**
 * Client-side provider that wraps NextIntlClientProvider
 * enabling seamless language switching without page reloads
 */
export function ClientI18nProvider({ children, initialLocale, initialMessages }: ClientI18nProviderProps) {
  const [locale, setLocale] = useState<Locale>(initialLocale);
  const [messages, setMessages] = useState<AbstractIntlMessages>(initialMessages);
  const [isLoading, setIsLoading] = useState(false);

  // Sync state if server props change (though typically they won't in a pure SPA transition)
  useEffect(() => {
    setLocale(initialLocale);
    setMessages(initialMessages);
  }, [initialLocale, initialMessages]);

  const changeLocale = useCallback(
    async (newLocale: Locale) => {
      if (newLocale === locale) return;

      setIsLoading(true);
      try {
        // 1. Fetch new messages from server
        const newMessages = await getMessagesForLocale(newLocale);

        // 2. Set the cookie client-side (for persistence on next reload)
        const expires = new Date();
        expires.setFullYear(expires.getFullYear() + 1);
        const fullLocaleCode = fullLocaleCodes[newLocale];
        document.cookie = `lang_code=${fullLocaleCode}; path=/; expires=${expires.toUTCString()}; SameSite=Lax`;

        // 3. Update state to trigger re-render with new language
        setMessages(newMessages);
        setLocale(newLocale);

        // 4. Update document direction and language for RTL support
        document.documentElement.lang = newLocale;
        document.documentElement.dir = isRtlLocale(newLocale) ? "rtl" : "ltr";

        // Note: We deliberately do NOT call router.refresh() here to avoid unmounting
        // the current page components (like active meeting connections)
      } catch (error) {
        console.error("Failed to switch locale:", error);
      } finally {
        setIsLoading(false);
      }
    },
    [locale]
  );

  return (
    <I18nContext.Provider value={{ locale, changeLocale, isLoading }}>
      <NextIntlClientProvider locale={locale} messages={messages} timeZone="UTC">
        {children}
      </NextIntlClientProvider>
    </I18nContext.Provider>
  );
}
